<template>
    <div>
        <div v-if="loading" class="w-screen h-screen fixed top-0 left-0 flex justify-center items-center z-9999">
            <div class="absolute top-0 left-0 w-full h-full bg-base-300 opacity-80" />
            <span class="loading loading-spinner loading-lg" />
        </div>
        <div class="flex flex-col gap-3 font-semibold text-sm">
            <div class="flex flex-row gap-3 items-center">
                <MiscIcon name="language-globe" size="md" class="language-globe" />
                <div class="text-base font-semibold">
                    {{ $t('layout.language.switch.headline') }}
                </div>
            </div>

            <div class="flex flex-col justify-between gap-3">
                <div v-if="computedPlatformLanguages.length > 1" class="grid grid-cols-2 gap-3" role="radiogroup">
                    <template v-for="language in computedPlatformLanguages" :key="language.id">
                        <div class="form-control">
                            <label class="cursor-pointer">
                                <input
                                    v-model="selectedLanguage"
                                    type="radio"
                                    name="language"
                                    :value="language.route"
                                    class="sr-only peer"
                                >
                                <span class="block border text-center border-base-500 p-2 peer-checked:border-secondary-content transition">
                                    {{ language.translatedName }}
                                </span>
                            </label>
                        </div>
                    </template>
                </div>
                <div>
                    <div class="flex items-center justify-evenly gap-5 border text-center border-base-500 px-5 py-2">
                        <div
                            v-if="selectedCountry"
                            class="form-control"
                            aria-labelledby="currentShopLabel"
                            role="button"
                            tabindex="0"
                            @click="toggleCountryDropdown = !toggleCountryDropdown"
                            @keydown.enter="toggleCountryDropdown = !toggleCountryDropdown"
                        >
                            <div id="currentShopLabel" class="cursor-pointer flex items-center gap-3">
                                <span>{{ t('layout.language.switch.currentShop') }}</span>
                                <MiscIcon
                                    :name="`flag-${selectedCountry}`"
                                    size="sm"
                                    class="inline-flex country-flag"
                                />
                                <span>{{ $t(`layout.language.switch.country-${selectedCountry.toUpperCase()}`) }}</span>
                                <MiscIcon
                                    name="chevron-down"
                                    size="md"
                                    class="w-6 h-6 shrink-0 transition-transform duration-300"
                                    :class="{ 'rotate-180': toggleCountryDropdown }"
                                />
                            </div>
                        </div>
                    </div>

                    <div
                        class="flex flex-col w-full overflow-hidden overflow-y-scroll transition-all duration-300 ease-in-out"
                        :class="toggleCountryDropdown ? 'max-h-50' : 'max-h-0'"
                        role="radiogroup"
                    >
                        <div
                            v-for="(country, index) in computedPlatformCountries"
                            :key="index"
                            class="text-center divide-x border-b border-base-500 px-5 py-2"
                        >
                            <label class="cursor-pointer flex gap-3 justify-start items-center">
                                <input
                                    v-model="selectedCountry"
                                    type="radio"
                                    name="country"
                                    class="sr-only peer"
                                    :value="country.code"
                                >
                                <MiscIcon
                                    :name="`flag-${country.code}`"
                                    size="sm"
                                    class="country-flag mr-2"
                                />
                                <span>{{ country.translatedName }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { PlatformLanguage } from '~/types/platform'

const { vueApp, $config } = useNuxtApp()
const { currentRoute } = useRouter()
const { apiClient } = useShopwareContext()

const loading = ref(false)
const { t, locale } = useI18n()

// Same useAsyncData key that `app/pages/{index,[...dynamic]}.vue` use to store the resolved
// SEO URL of the current page. Reading it here lets us build the target-language seo_url
// filter from language-invariant fields (foreignKey + routeName) instead of the
// current-language seoPathInfo, which can't match in the target language context.
const cachedRoutePath = currentRoute.value.path
    .replace(new RegExp('\\b' + locale.value + '\\b'), '')
    .replace('//', '/')
const { data: cachedPageData } = useNuxtData<[Schemas['SeoUrl'] | null, unknown]>(
    `cmsResponse${cachedRoutePath}`
)
const {
    platformLanguages,
    salesChannelDomains,
    platformDefaults,
    platformCountries,
    defaultLocale: runtimeConfigDefaultLocale
} = useRuntimeConfig().public
const defaultCountry = platformCountries.find((country) => {
    return country.id === platformDefaults.countryId
})?.iso.toLowerCase() as string

// all4golf.com is the only instance with env variable DEFAULT_LOCALE = 'en' which overrides the platform default language
// since this instance has not its own saleschannel it cannot rely on the platform default language
// this is needed to make work: all4golf.com -> user switch language to de -> component generates all4golf.com/de -> redirects to all4golf.de
const defaultLocale = runtimeConfigDefaultLocale === 'en'
    ? 'en'
    : (platformLanguages.find((lang) => lang.id === platformDefaults.languageId) as PlatformLanguage)?.route

const toggleCountryDropdown = ref(false)

// Languages
type LanguageDisplay = Omit<PlatformLanguage, 'code'> & { translatedName: string }

const selectedLanguage = ref()
const computedPlatformLanguages = computed(() => {
    const array: LanguageDisplay[] = []

    // Note: platformLanguages is already filtered by the sales channel module
    // based on the country-specific language configuration from Shopware Admin.
    // Each country only shows its allowed languages.
    platformLanguages.map((lang) => {
        const { code, ...rest } = lang

        array.push({
            translatedName: t(`layout.language.switch.language-${code}`),
            ...rest
        })
    })

    return array.sort(sortByNameAsc)
})

// Countries
const selectedCountry = ref<string>('')
const computedPlatformCountries = computed(() => {
    const array: { translatedName: string, code: string }[] = []

    Object.keys(salesChannelDomains).map((code) => {
        array.push({
            translatedName: t(`layout.language.switch.country-${code.toUpperCase()}`),
            code: code.toLowerCase()
        })
    })

    return array.sort(sortByNameAsc)
})

onMounted(async () => {
    selectedLanguage.value = vueApp.config.globalProperties.$i18n.locale
    selectedCountry.value = defaultCountry
})

watch (
    selectedLanguage,
    async () => {
        await submit()
    }
)

watch (
    selectedCountry,
    async () => {
        toggleCountryDropdown.value = false
        await submit()
    }
)

async function submit() {
    // start loading
    loading.value = true
    let countryChanged = false
    let languageChanged = false
    let route = ''

    // if language is going to change
    if (selectedLanguage.value != vueApp.config.globalProperties.$i18n.locale) {
        languageChanged = true
    }

    // if country is going to change
    if (selectedCountry.value != defaultCountry) {
        countryChanged = true
    }

    if (countryChanged) {
        // link to domain root — prefix_except_default means the default locale has no URL prefix
        const domain = Object.entries(salesChannelDomains).find(([k]) => k === selectedCountry.value)?.[1]
        route = `${domain ?? ''}/`
    } else if (!countryChanged && languageChanged) {
        // just change language
        const currentPath = window.location.pathname
        const currentQuery = window.location.search
        const baseURL = $config?.app?.baseURL || '/'

        // Remove baseURL and language prefix
        let pathWithoutBase = currentPath.replace(baseURL, '')
        if (pathWithoutBase.startsWith('/')) {
            pathWithoutBase = pathWithoutBase.substring(1)
        }

        const pathParts = pathWithoutBase.split('/')
        const match = platformLanguages.find((lang) => lang.route === pathParts[0])
        let pathWithoutLanguage = pathWithoutBase

        // Remove the language prefix
        if (match) {
            pathParts.shift()
            pathWithoutLanguage = pathParts.join('/')
        }

        const isHomePage = pathWithoutLanguage === '' || pathWithoutLanguage === '/'

        // Special handling for Nuxt-only routes (lexicon, etc.)
        if (typeof currentRoute?.value?.name === 'string' && currentRoute?.value?.name?.includes('lexicon')) {
            const switchLocalePath = useSwitchLocalePath()
            route = `${window.location.origin}${baseURL === '/' ? '' : baseURL}${switchLocalePath(selectedLanguage.value)}`
        } else {
            // Resolve the target-language seoPathInfo using the cached SEO result for the
            // current page. foreignKey + routeName identify the entity language-invariantly,
            // so the lookup can succeed in the target language context.
            let targetPath = ''
            const targetLanguageId = platformLanguages.find((lang) => lang.route === selectedLanguage.value)?.id
            const cachedSeoResult = cachedPageData.value?.[0]

            if (!isHomePage && targetLanguageId && cachedSeoResult?.foreignKey && cachedSeoResult?.routeName) {
                try {
                    const seoResponse = await apiClient.invoke("readSeoUrl post /seo-url", {
                        headers: {
                            'sw-language-id': targetLanguageId
                        },
                        body: {
                            filter: [
                                { type: "equals", field: "foreignKey", value: cachedSeoResult.foreignKey },
                                { type: "equals", field: "routeName", value: cachedSeoResult.routeName },
                            ],
                            limit: 1,
                        },
                    })

                    if (seoResponse?.data?.elements?.length > 0) {
                        targetPath = seoResponse.data.elements[0]?.seoPathInfo ?? ''
                    }
                } catch (error) {
                    console.warn('SEO URL resolution failed, using fallback', error)
                }
            }

            // Fallback to simple prefix replacement
            if (!targetPath && pathWithoutLanguage) {
                targetPath = pathWithoutLanguage
            }

            // Build final URL — prefix_except_default: default locale has no URL prefix
            const basePath = baseURL === '/' ? '' : baseURL
            const isDefaultLocale = selectedLanguage.value === defaultLocale
            route = isDefaultLocale
                ? `${window.location.origin}${basePath}${targetPath ? '/' + targetPath : ''}${currentQuery || ''}`
                : `${window.location.origin}${basePath}/${selectedLanguage.value}${targetPath ? '/' + targetPath : ''}${currentQuery || ''}`
        }
    } else {
        // do nothing
        loading.value = false
        return
    }

    window.location.href = route
}

function sortByNameAsc (a: { translatedName: string }, b: { translatedName: string }) {
    if ( a.translatedName < b.translatedName ){
        return -1
    }
    if ( a.translatedName > b.translatedName ){
        return 1
    }
    return 0
}
</script>
